import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dumbbell, Eye, EyeOff, CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying | valid | invalid | success
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    axios.get(`${API}/auth/verify-reset-token?token=${token}`)
      .then(res => {
        setEmail(res.data.email);
        setStatus('valid');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setStatus('success');
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button onClick={() => navigate('/auth')} className="flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors" data-testid="back-to-auth-btn">
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </button>

        <div className="text-center mb-8">
          <Dumbbell className="w-14 h-14 text-blood-500 mx-auto mb-3" />
          <h1 className="text-3xl font-bold text-blood-400 mb-1 tracking-wider">ANVIL & ASH</h1>
          <p className="text-stone-400 uppercase tracking-widest text-xs">Reset Your Password</p>
        </div>

        <Card className="glass p-6 border border-stone-800" data-testid="reset-password-card">

          {status === 'verifying' && (
            <div className="text-center py-8" data-testid="reset-verifying">
              <Loader2 className="w-10 h-10 text-blood-400 mx-auto mb-4 animate-spin" />
              <p className="text-stone-400">Verifying your reset link...</p>
            </div>
          )}

          {status === 'invalid' && (
            <div className="text-center py-8" data-testid="reset-invalid">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-blood-400 mb-2">Invalid or Expired Link</h2>
              <p className="text-stone-400 mb-6 text-sm">
                This password reset link is no longer valid. Please request a new one.
              </p>
              <Button
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold tracking-wider"
                data-testid="reset-go-login"
              >
                BACK TO LOGIN
              </Button>
            </div>
          )}

          {status === 'valid' && (
            <form onSubmit={handleReset} className="space-y-5" data-testid="reset-form">
              <p className="text-stone-400 text-sm mb-2">
                Enter a new password for <span className="text-blood-400 font-medium">{email}</span>
              </p>
              <div>
                <Label className="text-blood-400 uppercase tracking-wider text-xs">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    required
                    minLength={6}
                    className="bg-stone-900 border-stone-700 text-white pr-10"
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-blood-400 uppercase tracking-wider text-xs">Confirm Password</Label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  className="bg-stone-900 border-stone-700 text-white mt-1"
                  data-testid="reset-confirm-password"
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-5 rounded-xl tracking-wider"
                disabled={isLoading || newPassword !== confirmPassword}
                data-testid="reset-submit-btn"
              >
                {isLoading ? 'RESETTING...' : 'SET NEW PASSWORD'}
              </Button>
            </form>
          )}

          {status === 'success' && (
            <div className="text-center py-8" data-testid="reset-success">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-blood-400 mb-2">Password Reset Complete</h2>
              <p className="text-stone-400 mb-6 text-sm">
                Your password has been updated. You can now log in with your new password.
              </p>
              <Button
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold tracking-wider"
                data-testid="reset-go-login-success"
              >
                GO TO LOGIN
              </Button>
            </div>
          )}

        </Card>
      </div>
    </div>
  );
}
